// src/features/fx-info/components/FxTrendExtensionChart.tsx
//
// Trend Extension & Regime Shift Oscillator — "The Rubber Band".
//
// A universally-available replacement/complement for the COT positioning pane:
// because the CFTC only tracks currency futures on deep exchanges (CME), Tier-3
// EM currencies (IDR, PHP, CLP, THB, ...) and some Tier-2s have ZERO COT flow
// data. This oscillator instead derives its signal purely from the close /
// sma_50 / sma_200 the backend already sends, so it populates for every one of
// the 30 currencies in the universe — no backend changes, no empty states.
//
// Two synchronized panes (syncId="fx-master-sync", so the crosshair snaps to
// the exact close / drawdown / frac-diff of the Master Time-Series above):
//   1. Price Mean-Reversion — % distance of close from its 200d SMA. Bars flash
//      red/green once the stretch exceeds the +/-5% over-extension threshold.
//   2. Regime Shift Oscillator — the 50d-vs-200d SMA spread. Crossing 0 visually
//      flags a Golden Cross (up) or Death Cross (down).
//
// Performance discipline for ~1,300+ daily rows: isAnimationActive={false}
// everywhere (no per-bar animation over thousands of nodes).

import { useMemo, useRef } from 'react';
import {
    Area,
    Bar,
    CartesianGrid,
    Cell,
    ComposedChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { FxTimeSeriesPoint } from '../api';
import { ChartDownloadMenu } from '../../../shared/ui';
import { useChartExport } from '../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../shared/utils/chartFilename';

interface Props {
    data: FxTimeSeriesPoint[];
}

export const FxTrendExtensionChart = ({ data }: Props) => {
    const captureRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(captureRef);

    // 1. Calculate the structural extension metrics dynamically in the frontend.
    const chartData = useMemo(() => {
        return data.map((d) => {
            // Mean Reversion Risk: % distance of Close from 200d SMA.
            const price_vs_200 =
                d.sma_200 && d.close ? d.close / d.sma_200 - 1.0 : null;

            // Trend Intensity: % distance of 50d SMA from 200d SMA.
            const sma50_vs_200 =
                d.sma_50 && d.sma_200 ? d.sma_50 / d.sma_200 - 1.0 : null;

            return {
                ...d,
                price_vs_200,
                sma50_vs_200,
                displayDate: new Date(d.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                }),
                axisDate: new Date(d.date).toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit',
                }),
            };
        });
    }, [data]);

    const formatPct = (val: number) =>
        new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            signDisplay: 'always',
        }).format(val);

    const tooltipStyle = {
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '8px',
        color: '#f8fafc',
        fontSize: '13px',
    };
    // Recharts colours tooltip rows with the series colour by default, so the
    // neutral slate bars render nearly invisible against the dark tooltip
    // background. Force a light, high-contrast colour on both the label and
    // item rows so "Distance from 200d SMA" is always legible.
    const tooltipLabelStyle = { color: '#f8fafc', fontWeight: 600 };
    const tooltipItemStyle = { color: '#e2e8f0' };

    if (!data.length) return null;

    return (
        <div className="w-full flex flex-col gap-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                        Trend Extension & Regime Shift
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        Measures structural over-extension by tracking the
                        percentage distance from macroeconomic moving averages.
                    </p>
                </div>
                <ChartDownloadMenu
                    label="Trend Extension & Regime Shift chart"
                    onPng={() => downloadPng({ filename: buildChartFilename('fx-trend-extension') })}
                    onJpeg={() => downloadJpeg({ filename: buildChartFilename('fx-trend-extension') })}
                />
            </div>

            <div ref={captureRef} className="flex flex-col gap-6 bg-white dark:bg-slate-900">
            {/* Top Chart: Price vs 200d SMA (Mean Reversion) */}
            <div className="w-full h-[220px]">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 ml-4">
                    Price Extension (vs 200d SMA)
                </p>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        syncId="fx-master-sync"
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis
                            dataKey="axisDate"
                            minTickGap={50}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            tickFormatter={formatPct}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            orientation="right"
                            width={60}
                        />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelStyle={tooltipLabelStyle}
                            itemStyle={tooltipItemStyle}
                            cursor={{ fill: '#334155', opacity: 0.2 }}
                            labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.displayDate || label
                            }
                            formatter={(val: number) => [
                                formatPct(val),
                                'Distance from 200d SMA',
                            ]}
                        />

                        <ReferenceLine
                            y={0}
                            stroke="#475569"
                            strokeWidth={1.5}
                        />
                        {/* Institutional Warning Bands (+/- 5%) */}
                        <ReferenceLine
                            y={0.05}
                            stroke="#ef4444"
                            strokeDasharray="3 3"
                            opacity={0.5}
                            label={{
                                position: 'insideTopLeft',
                                value: '+5% (Overbought)',
                                fill: '#ef4444',
                                fontSize: 10,
                            }}
                        />
                        <ReferenceLine
                            y={-0.05}
                            stroke="#10b981"
                            strokeDasharray="3 3"
                            opacity={0.5}
                            label={{
                                position: 'insideBottomLeft',
                                value: '-5% (Oversold)',
                                fill: '#10b981',
                                fontSize: 10,
                            }}
                        />

                        {/* Bar chart coloured dynamically based on extremes. */}
                        <Bar
                            dataKey="price_vs_200"
                            isAnimationActive={false}
                            maxBarSize={4}
                        >
                            {chartData.map((entry, index) => {
                                const dist = entry.price_vs_200 || 0;
                                // Dark slate for neutral; bright red/green for
                                // extreme stretches beyond the +/-5% threshold.
                                const color =
                                    dist > 0.05
                                        ? '#ef4444'
                                        : dist < -0.05
                                          ? '#10b981'
                                          : '#64748b';
                                return (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={color}
                                        fillOpacity={
                                            Math.abs(dist) > 0.05 ? 0.9 : 0.4
                                        }
                                    />
                                );
                            })}
                        </Bar>
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Bottom Chart: 50d vs 200d SMA Spread (Regime Intensity) */}
            <div className="w-full h-[140px]">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 ml-4">
                    Regime Shift Oscillator (50d vs 200d Spread)
                </p>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        syncId="fx-master-sync"
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis dataKey="axisDate" hide />
                        <YAxis
                            tickFormatter={formatPct}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            orientation="right"
                            width={60}
                        />
                        <Tooltip
                            contentStyle={tooltipStyle}
                            labelStyle={tooltipLabelStyle}
                            itemStyle={tooltipItemStyle}
                            labelFormatter={(label, payload) =>
                                payload?.[0]?.payload?.displayDate || label
                            }
                            formatter={(val: number) => [
                                formatPct(val),
                                'SMA Spread',
                            ]}
                        />

                        <ReferenceLine
                            y={0}
                            stroke="#475569"
                            strokeWidth={1.5}
                            label={{
                                position: 'insideBottomLeft',
                                value: 'Golden / Death Cross Line',
                                fill: '#64748b',
                                fontSize: 10,
                            }}
                        />
                        <Area
                            type="step"
                            dataKey="sma50_vs_200"
                            stroke="#6366f1"
                            fill="#6366f1"
                            fillOpacity={0.2}
                            isAnimationActive={false}
                            connectNulls
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            </div>
        </div>
    );
};

export default FxTrendExtensionChart;
